import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAlfredInOutreachEnabled } from '../../../lib/alfred-in-outreach-flag';
import { connectorDisplayName, fetchConnectors, type ConnectorsStatus } from '../../../lib/connectors';
import {
  HOME_TAB_BI_PAGE,
  HOME_TAB_CONNECTOR_HINTS,
  homeTabConnectBiName,
  homeTabConnectorHints,
  splitConnectHint,
  type HomeTabWithConnectHint,
} from '../../../lib/home-tab-connectors';
import { useTranslation } from '../../../lib/i18n';
import { queryKeys } from '../../../lib/query-client';
import { useConnectConnector } from '../../../lib/use-connect-connector';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { UpgradeButton } from '../connectors/primitives';
import { TextButton } from '../ui';
import styles from './CategoryView.module.css';

/**
 * The line under a Home tab's title. While the tab still has apps to offer it reads "To turn emails and
 * meetings into tasks, connect Gmail Google Calendar" — each app an underlined DS text link — and pressing
 * an app starts its connect right there; otherwise it is the tab's own description (`fallback`). The rule
 * is `homeTabConnectorHints`.
 *
 * It reads the shared `queryKeys.connectors` entry, the one the Connectors screen's controller writes
 * through on every re-read, so a connect made there has already updated this line when Back lands here.
 */
export function TabConnectHint({ tab, fallback }: { tab: HomeTabWithConnectHint; fallback: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const alfredOnOutreach = useAlfredInOutreachEnabled();
  const queryClient = useQueryClient();
  const openSideChat = useOpenSideChat();
  const { data, isLoading, isError } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 });
  useLoadBi('home-tab-connect-hint-data', { isLoading, isError });
  // The Connectors screen's own connect, run in place: the re-read after it lands drops the app from the line.
  const { busy, actionError, actionUpgrade, connect } = useConnectConnector({
    getConnectors: () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.connectors ?? [],
    getGmailScopesEnabled: () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.gmailScopesEnabled ?? false,
    refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors }),
    openChat: openSideChat,
  });

  const offered = data?.enabled ? homeTabConnectorHints(tab, data.connectors, alfredOnOutreach) : [];
  if (offered.length === 0) return <>{fallback}</>;

  // The slot is passed back as itself so the copy can be split around it.
  const { before, after } = splitConnectHint(t(HOME_TAB_CONNECTOR_HINTS[tab].hintKey, { apps: '{apps}' }));
  // Figma 21946:42239: the text, then one DS Text button per app — underlined, no trailing glyph — 8 apart.
  // Each piece wraps whole, so on a narrow pane the apps drop to their own line.
  return (
    <span className={styles.hint}>
      {before.trim() && <span>{before.trim()}</span>}
      {offered.map((c) => (
        <TextButton
          key={c.toolkit}
          size="small"
          underline
          loading={busy.has(c.toolkit)}
          onClick={() => {
            logPageClickBi({ page_name: HOME_TAB_BI_PAGE, button_name: homeTabConnectBiName(tab, c.toolkit) });
            connect(c);
          }}
        >
          {connectorDisplayName(c)}
        </TextButton>
      ))}
      {after.trim() && <span>{after.trim()}</span>}
      {actionError && (
        <span role="alert" className={styles.hintError}>
          {actionError}
          {actionUpgrade && <UpgradeButton />}
        </span>
      )}
    </span>
  );
}
